
import React, { useEffect, useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { cities, industries } from '@/data/cityIndustryData';
import { getAllCityIndustryUrls } from '@/components/CityIndustryRouteHandler';

const Sitemap = () => {
  const [cityIndustryUrls, setCityIndustryUrls] = useState<string[]>([]);
  
  useEffect(() => {
    // Generate city-industry URLs
    const urls = getAllCityIndustryUrls();
    setCityIndustryUrls(urls);
  }, []);

  return (
    <PageLayout
      title="Website Sitemap | Astrem"
      description="Complete list of all pages on the Astrem website including our web design services for various industries in Louisiana cities."
      className="dark"
    >
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h1 className="text-4xl md:text-5xl font-display font-medium mb-8 text-foreground">Website Sitemap</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mb-12">
              Below you'll find a complete list of all pages on our website, organized by category. 
              This includes our main pages, service pages, location pages, and our specialized web design pages 
              for various industries in Louisiana cities.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
            <ScrollReveal>
              <h2 className="text-2xl font-display font-medium mb-4 text-foreground">Main Pages</h2>
              <ul className="space-y-2">
                <li><Link to="/" className="text-blue-400 hover:underline">Home</Link></li>
                <li><Link to="/about" className="text-blue-400 hover:underline">About Us</Link></li>
                <li><Link to="/services" className="text-blue-400 hover:underline">Services</Link></li>
                <li><Link to="/portfolio" className="text-blue-400 hover:underline">Portfolio</Link></li>
                <li><Link to="/contact" className="text-blue-400 hover:underline">Contact</Link></li>
                <li><Link to="/blog" className="text-blue-400 hover:underline">Blog</Link></li>
                <li><Link to="/privacy-policy" className="text-blue-400 hover:underline">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-blue-400 hover:underline">Terms of Service</Link></li>
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <h2 className="text-2xl font-display font-medium mb-4 text-foreground">Service Pages</h2>
              <ul className="space-y-2">
                <li><Link to="/services/website-design" className="text-blue-400 hover:underline">Website Design</Link></li>
                <li><Link to="/services/photography" className="text-blue-400 hover:underline">Photography</Link></li>
                <li><Link to="/services/marketing" className="text-blue-400 hover:underline">Marketing</Link></li>
                <li><Link to="/services/seo" className="text-blue-400 hover:underline">SEO Services</Link></li>
                <li><Link to="/services/branding" className="text-blue-400 hover:underline">Branding</Link></li>
                <li><Link to="/services/software" className="text-blue-400 hover:underline">Software</Link></li>
                <li><Link to="/services/real-estate-media" className="text-blue-400 hover:underline">Real Estate Media</Link></li>
                <li><Link to="/services/tech-coaching" className="text-blue-400 hover:underline">Tech Coaching</Link></li>
                <li><Link to="/services/virtual-tours" className="text-blue-400 hover:underline">Virtual Tours</Link></li>
                <li><Link to="/services/led-printing" className="text-blue-400 hover:underline">LED Printing</Link></li>
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <h2 className="text-2xl font-display font-medium mb-4 text-foreground">Location Pages</h2>
              <ul className="space-y-2">
                <li><Link to="/locations/baton-rouge" className="text-blue-400 hover:underline">Baton Rouge</Link></li>
                <li><Link to="/locations/gonzales" className="text-blue-400 hover:underline">Gonzales</Link></li>
                <li><Link to="/locations/prairieville" className="text-blue-400 hover:underline">Prairieville</Link></li>
                <li><Link to="/locations/ascension-parish" className="text-blue-400 hover:underline">Ascension Parish</Link></li>
                <li><Link to="/locations/geismar" className="text-blue-400 hover:underline">Geismar</Link></li>
                <li><Link to="/locations/dutchtown" className="text-blue-400 hover:underline">Dutchtown</Link></li>
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <h2 className="text-2xl font-display font-medium mb-4 text-foreground">Web Design Pages</h2>
              <ul className="space-y-2">
                <li><Link to="/web-design" className="text-blue-400 hover:underline">Web Design</Link></li>
                <li><Link to="/web-design/directories" className="text-blue-400 hover:underline">Web Design Directories</Link></li>
                <li><Link to="/web-design-services" className="text-blue-400 hover:underline">Web Design Services</Link></li>
              </ul>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={250}>
            <h2 className="text-2xl font-display font-medium mt-12 mb-6 text-foreground">Industry & City Web Design Pages</h2>
            <p className="text-muted-foreground mb-6">
              Below are all our specialized web design pages for different industries in various Louisiana cities.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
              {cityIndustryUrls.map((url) => {
                const parts = url.split('/');
                const industrySlug = parts[2];
                const citySlug = parts[3];
                
                const industry = industries[industrySlug];
                const city = cities[citySlug];
                
                if (!industry || !city) return null;
                
                return (
                  <div key={url}>
                    <Link to={url} className="text-blue-400 hover:underline">
                      {industry.name} Web Design in {city.name}
                    </Link>
                  </div>
                );
              })}
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageLayout>
  );
};

export default Sitemap;
